import * as React from 'react';
import { cn } from '@noor/lib/cn';

export type SidebarIconProps = React.ComponentProps<'svg'>;

/**
 * Exact sidebar glyphs for the dark Noor CPanel sidebar (Figma node
 * 1181:27559). Export colors are normalized to `currentColor` — the icon
 * tile controls the paint (white on the active tile, #d6dff0 otherwise).
 * The Remix "line" icons below are exported on Figma's 20px grid (not the
 * stock 24px Remix grid); the logo and toggle glyphs keep their own grids.
 */

/** Noor "noor®" wordmark (77x28.23) for the 56px sidebar header. */
export function FigmaSidebarLogo({ className, ...props }: SidebarIconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 77 28.2353"
      fill="none"
      className={cn('block', className)}
      {...props}
    >
      <path
        d="M17.5522 27.722H12.7256V17.0993C12.7256 15.583 12.4607 14.4725 11.9333 13.7759C11.4059 13.0765 10.6725 12.7283 9.73601 12.7283C9.08779 12.7283 8.43418 12.8958 7.77303 13.2284C7.11189 13.5637 6.51751 14.022 5.9901 14.6061C5.46269 15.1901 5.07674 15.8658 4.83744 16.6278V27.722H0.0108613V8.69233H4.36928V12.2411C4.85037 11.4188 5.4627 10.7247 6.20624 10.1564C6.94979 9.58811 7.80912 9.15339 8.78154 8.85224C9.75415 8.5509 10.7806 8.39904 11.8611 8.39904C13.0369 8.39904 14.0043 8.6112 14.7608 9.03277C15.5171 9.45453 16.0935 10.0228 16.4897 10.7379C16.8859 11.4527 17.1612 12.2542 17.3181 13.1498C17.475 14.043 17.5522 14.9518 17.5522 15.8684V27.722Z"
        fill="#FF6D00"
      />
      <path
        d="M26.9326 23.9925C27.666 23.9925 28.4068 23.8249 29.1581 23.4845C29.9068 23.1439 30.6091 22.6594 31.2602 22.0257C31.9111 21.3918 32.4771 20.6506 32.9555 19.7994C33.4341 18.9482 33.7685 18.0238 33.9565 17.026C34.1261 16.1252 34.139 15.3341 33.9949 14.6532C33.8509 13.9723 33.5474 13.4406 33.0867 13.0634C32.6263 12.6864 32.0165 12.4951 31.2602 12.4951C30.5037 12.4951 29.791 12.6629 29.0527 13.0033C28.3142 13.3437 27.6144 13.8282 26.9506 14.4621C26.2869 15.0932 25.7157 15.8423 25.2346 16.7064C24.7535 17.5708 24.4191 18.5006 24.2311 19.4983C24.0665 20.3756 24.0563 21.1535 24.2003 21.8344C24.3444 22.5154 24.648 23.047 25.1086 23.4242C25.5691 23.8014 26.1789 23.9925 26.9351 23.9925H26.9326ZM25.4276 28.2353C23.6292 28.2353 22.2271 27.8634 21.2184 27.1248C20.2099 26.3864 19.5487 25.3988 19.2399 24.1575C18.9287 22.9187 18.9132 21.549 19.1963 20.0536C19.4922 18.4821 20.0505 16.9868 20.8737 15.5645C21.6969 14.1425 22.7209 12.8801 23.9455 11.7775C25.1704 10.675 26.5339 9.8107 28.0338 9.18739C29.5338 8.56408 31.1135 8.25232 32.7677 8.25507C34.5636 8.25507 35.9683 8.62694 36.9768 9.36537C37.9853 10.104 38.6466 11.0862 38.9604 12.3144C39.2744 13.5427 39.2873 14.9151 39.0017 16.4367C38.7161 17.9584 38.1474 19.5036 37.3241 20.9256C36.5009 22.3477 35.477 23.6128 34.2523 24.7127C33.0277 25.8153 31.6641 26.6796 30.1641 27.303C28.664 27.9263 27.0845 28.2378 25.4301 28.2353H25.4276Z"
        fill="#FF6D00"
      />
      <path
        d="M46.738 23.9925C47.4714 23.9925 48.2122 23.8249 48.9635 23.4845C49.7122 23.1439 50.4147 22.6594 51.0654 22.0257C51.7165 21.3918 52.2825 20.6506 52.7609 19.7994C53.2395 18.9482 53.5739 18.0238 53.7619 17.026C53.9315 16.1252 53.9444 15.3341 53.8003 14.6532C53.6563 13.9723 53.3528 13.4406 52.8921 13.0634C52.4317 12.6864 51.8219 12.4951 51.0654 12.4951C50.3091 12.4951 49.5964 12.6629 48.8581 13.0033C48.1196 13.3437 47.4198 13.8282 46.756 14.4621C46.0923 15.0932 45.5211 15.8423 45.04 16.7064C44.5589 17.5708 44.2245 18.5006 44.0365 19.4983C43.8719 20.3756 43.8617 21.1535 44.0057 21.8344C44.1498 22.5154 44.4534 23.047 44.914 23.4242C45.3745 23.8014 45.9843 23.9925 46.7405 23.9925H46.738ZM45.233 28.2353C43.4346 28.2353 42.0325 27.8634 41.0239 27.1248C40.0153 26.3864 39.3541 25.3988 39.0453 24.1575C38.7341 22.9187 38.7186 21.549 39.0017 20.0536C39.2976 18.4821 39.8558 16.9868 40.6791 15.5645C41.5023 14.1425 42.5263 12.8801 43.7511 11.7775C44.9758 10.675 46.3393 9.8107 47.8392 9.18739C49.3392 8.56408 50.9189 8.25232 52.5731 8.25507C54.369 8.25507 55.7737 8.62694 56.7822 9.36537C57.7909 10.104 58.452 11.0862 58.7658 12.3144C59.0798 13.5427 59.0927 14.9151 58.8071 16.4367C58.5215 17.9584 57.9528 19.5036 57.1295 20.9256C56.3063 22.3477 55.2824 23.6128 54.0577 24.7127C52.8331 25.8153 51.4695 26.6796 49.9695 27.303C48.4694 27.9263 46.8899 28.2378 45.2355 28.2353H45.233Z"
        fill="#FF6D00"
      />
      <path
        d="M71.5707 12.7623C70.1068 12.7623 68.7972 13.0505 67.6446 13.6239C66.4919 14.1975 65.6636 15.0226 65.1592 16.0989V27.722H60.3328V8.69239H64.763V12.6156C65.4346 11.2957 66.2887 10.2559 67.3204 9.49899C68.3522 8.74209 69.4456 8.32583 70.5981 8.25236H71.2104C71.3546 8.25236 71.4756 8.26552 71.5707 8.2891V12.7623Z"
        fill="#FF6D00"
      />
      <path
        d="M74.2297 3.59844L75.0298 5.13836H74.2554L73.4732 3.59844H74.2297ZM72.2899 5.14111V1.87249H73.9157C74.047 1.87249 74.1886 1.90923 74.343 1.98506C74.4974 2.06108 74.6284 2.17639 74.7365 2.3308C74.8445 2.4854 74.8986 2.68184 74.8986 2.91757C74.8986 3.1533 74.842 3.36016 74.7287 3.52517C74.6157 3.69018 74.4792 3.81846 74.3171 3.90234C74.1577 3.98858 74.0059 4.03062 73.8619 4.03062H72.6886V3.49884H73.6458C73.7409 3.49884 73.8438 3.44914 73.957 3.3523C74.0676 3.25545 74.1242 3.10871 74.1242 2.91482C74.1242 2.72113 74.0676 2.57969 73.957 2.50897C73.8465 2.43825 73.7486 2.40151 73.6637 2.40151H72.9948V5.13836H72.2899V5.14111ZM73.5068 6.23844C73.9929 6.23844 74.4381 6.11782 74.8368 5.87443C75.2357 5.63339 75.5547 5.30867 75.794 4.90007C76.0306 4.49147 76.1516 4.04103 76.1516 3.54599C76.1516 3.05115 76.0333 2.59796 75.794 2.19211C75.5572 1.78351 75.2382 1.46134 74.8368 1.21775C74.4356 0.976911 73.9929 0.853741 73.5068 0.853741C73.0205 0.853741 72.5753 0.974161 72.1766 1.21775C71.7752 1.45879 71.4562 1.78351 71.2194 2.19211C70.9828 2.59796 70.8618 3.05115 70.8618 3.54599C70.8618 4.04103 70.9803 4.49422 71.2194 4.90007C71.4562 5.30612 71.7752 5.63084 72.1766 5.87443C72.5753 6.11527 73.0205 6.23844 73.5068 6.23844ZM73.5068 7.08687C73.0257 7.08687 72.5753 6.99533 72.1535 6.81185C71.7341 6.62857 71.3636 6.37457 71.0446 6.04985C70.7256 5.72513 70.4761 5.34796 70.2958 4.92109C70.1158 4.49422 70.0257 4.03317 70.0257 3.54344C70.0257 3.0537 70.1158 2.5954 70.2958 2.16578C70.4759 1.73636 70.7256 1.36174 71.0446 1.03702C71.3636 0.712302 71.7341 0.458302 72.1535 0.27502C72.5728 0.0915427 73.0257 0 73.5068 0C73.9879 0 74.4381 0.0915427 74.86 0.27502C75.2793 0.458302 75.6498 0.712302 75.9688 1.03702C76.288 1.36174 76.5375 1.73891 76.7176 2.16578C76.8976 2.59265 76.9878 3.0537 76.9878 3.54344C76.9878 4.03317 76.8976 4.49147 76.7176 4.92109C76.5375 5.34796 76.288 5.72513 75.9688 6.04985C75.6498 6.37457 75.2793 6.62857 74.86 6.81185C74.4406 6.99533 73.9879 7.08687 73.5068 7.08687Z"
        fill="#FF6D00"
      />
    </svg>
  );
}

/** Figma "Business, Products/Business, Chart" (20px, stroke 1.2) — Мерчант. */
export function FigmaMerchantChartIcon({ className, ...props }: SidebarIconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="none"
      className={cn('size-5 shrink-0', className)}
      {...props}
    >
      <path
        d="M3.33333 14.1225V15.8333C3.33333 16.7542 4.07917 17.5 5 17.5H15C15.9208 17.5 16.6667 16.7542 16.6667 15.8333V8.95333"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3.33333 11.2058V4.16667C3.33333 3.24583 4.07917 2.5 5 2.5H15C15.9208 2.5 16.6667 3.24583 16.6667 4.16667V6.03667"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4.35953 11.633C4.92905 12.2025 4.92905 13.1259 4.35953 13.6954C3.79002 14.2649 2.86665 14.2649 2.29714 13.6954C1.72763 13.1259 1.72763 12.2025 2.29714 11.633C2.86666 11.0635 3.79003 11.0635 4.35953 11.633"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.7908 12.4975C10.7908 11.6925 11.4433 11.0392 12.2492 11.0392C13.055 11.0392 13.7075 11.6917 13.7075 12.4975C13.7075 13.3033 13.055 13.9558 12.2492 13.9558C11.4467 13.9592 10.7942 13.3117 10.7908 12.5092C10.7908 12.505 10.7908 12.5017 10.7908 12.4975Z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8.8545 6.4638C9.42402 7.03332 9.42402 7.95668 8.8545 8.52619C8.28499 9.09571 7.36162 9.09571 6.79211 8.52619C6.22259 7.95668 6.22259 7.03331 6.79211 6.4638C7.36162 5.89429 8.28499 5.89429 8.8545 6.4638"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M17.7028 6.4638C18.2723 7.03332 18.2723 7.95668 17.7028 8.52619C17.1333 9.09571 16.2099 9.09571 15.6404 8.52619C15.0709 7.95668 15.0709 7.03331 15.6404 6.4638C16.21 5.89429 17.1333 5.89429 17.7028 6.4638"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15.6583 8.54167L13.2417 11.4333"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4.31667 11.5917L6.83333 8.55833"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.2583 11.4333L8.81667 8.55833"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Remix "store-2-fill" (20px grid) — Бухгалтерия. */
export function RemixStore2FillIcon({ className, ...props }: SidebarIconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="none"
      className={cn('size-5 shrink-0', className)}
      {...props}
    >
      <path
        d="M18.3333 16.6667V18.3333H1.66667V16.6667H2.5V11.035C1.98698 10.6926 1.56643 10.2288 1.27568 9.6848C0.984939 9.14082 0.832993 8.53347 0.833333 7.91667C0.833333 7.2275 1.02 6.56333 1.36083 5.9975L3.62083 2.08333C3.69397 1.95665 3.79917 1.85146 3.92585 1.77832C4.05252 1.70518 4.19622 1.66667 4.3425 1.66667H15.6583C15.8046 1.66667 15.9483 1.70518 16.075 1.77832C16.2017 1.85146 16.3069 1.95665 16.38 2.08333L18.6317 5.985C19.1288 6.81073 19.2886 7.79628 19.0778 8.73678C18.8671 9.67729 18.302 10.5004 17.5 11.035V16.6667H18.3333ZM4.82417 3.33333L2.79667 6.84417C2.59965 7.33095 2.59373 7.87419 2.78011 8.36515C2.96648 8.85611 3.3314 9.25858 3.80182 9.49199C4.27224 9.72541 4.81345 9.77257 5.31715 9.62401C5.82085 9.47546 6.24986 9.14217 6.51833 8.69083C6.7975 7.99333 7.785 7.99333 8.065 8.69083C8.21942 9.07785 8.48633 9.40967 8.83127 9.64342C9.17621 9.87717 9.58332 10.0021 10 10.0021C10.4167 10.0021 10.8238 9.87717 11.1687 9.64342C11.5137 9.40967 11.7806 9.07785 11.935 8.69083C12.2142 7.99333 13.2017 7.99333 13.4817 8.69083C13.5898 8.95704 13.7519 9.198 13.9576 9.39856C14.1634 9.59913 14.4084 9.75496 14.6773 9.85627C14.9462 9.95757 15.2331 10.0022 15.52 9.98724C15.807 9.97231 16.0878 9.89818 16.3447 9.76952C16.6016 9.64086 16.8291 9.46044 17.013 9.23961C17.1968 9.01878 17.333 8.7623 17.4129 8.48631C17.4928 8.21032 17.5148 7.92077 17.4775 7.63588C17.4401 7.35098 17.3442 7.07689 17.1958 6.83083L15.175 3.33333H4.825H4.82417Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Remix "briefcase-line" (20px grid) — Бизнес. */
export function RemixBriefcaseLineIcon({ className, ...props }: SidebarIconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="none"
      className={cn('size-5 shrink-0', className)}
      {...props}
    >
      <path
        d="M5.83333 4.16667V1.66667C5.83333 1.44565 5.92113 1.23369 6.07741 1.07741C6.23369 0.921131 6.44565 0.833333 6.66667 0.833333H13.3333C13.5543 0.833333 13.7663 0.921131 13.9226 1.07741C14.0789 1.23369 14.1667 1.44565 14.1667 1.66667V4.16667H17.5C17.721 4.16667 17.933 4.25446 18.0893 4.41074C18.2455 4.56702 18.3333 4.77899 18.3333 5V16.6667C18.3333 16.8877 18.2455 17.0996 18.0893 17.2559C17.933 17.4122 17.721 17.5 17.5 17.5H2.5C2.27899 17.5 2.06702 17.4122 1.91074 17.2559C1.75446 17.0996 1.66667 16.8877 1.66667 16.6667V5C1.66667 4.77899 1.75446 4.56702 1.91074 4.41074C2.06702 4.25446 2.27899 4.16667 2.5 4.16667H5.83333ZM3.33333 13.3333V15.8333H16.6667V13.3333H3.33333ZM3.33333 11.6667H16.6667V5.83333H3.33333V11.6667ZM7.5 2.5V4.16667H12.5V2.5H7.5ZM9.16667 9.16667H10.8333V10.8333H9.16667V9.16667Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Remix "inbox-archive-line" (20px grid) — ОПС. */
export function RemixInboxArchiveLineIcon({ className, ...props }: SidebarIconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="none"
      className={cn('size-5 shrink-0', className)}
      {...props}
    >
      <path
        d="M3.33333 2.5H16.6667L18.3333 5.83333V16.6667C18.3333 16.8877 18.2455 17.0996 18.0893 17.2559C17.933 17.4122 17.721 17.5 17.5 17.5H2.5C2.27899 17.5 2.06702 17.4122 1.91074 17.2559C1.75446 17.0996 1.66667 16.8877 1.66667 16.6667V5.83667L3.33333 2.5ZM16.6667 7.5H3.33333V15.8333H16.6667V7.5ZM16.47 5.83333L15.6367 4.16667H4.36417L3.53083 5.83333H16.47ZM10.8333 11.6667H13.3333L10 15L6.66667 11.6667H9.16667V8.33333H10.8333V11.6667Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Remix "send-plane-line" (20px grid) — Доставка. */
export function RemixSendPlaneLineIcon({ className, ...props }: SidebarIconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="none"
      className={cn('size-5 shrink-0', className)}
      {...props}
    >
      <path
        d="M1.6025 7.80833C1.1775 7.6375 1.1825 7.38333 1.63083 7.23417L17.5358 1.9325C17.9767 1.78583 18.2292 2.0325 18.1058 2.46417L13.5608 18.3692C13.4358 18.81 13.165 18.83 12.9633 18.4275L9.16667 10.8333L1.6025 7.80833ZM5.6775 7.64167L10.3742 9.52083L12.9075 14.5892L15.8625 4.2475L5.67667 7.64167H5.6775Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Remix "service-line" (20px grid) — Мерчанты (Еда). */
export function RemixServiceLineIcon({ className, ...props }: SidebarIconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="none"
      className={cn('size-5 shrink-0', className)}
      {...props}
    >
      <path
        d="M2.63417 3.72417C3.60089 2.75727 4.8975 2.19174 6.26383 2.14104C7.63016 2.09035 8.96512 2.55824 10.0008 3.45083C11.0356 2.55973 12.3688 2.09236 13.7335 2.1423C15.0982 2.19224 16.3937 2.75581 17.3606 3.72017C18.3274 4.68453 18.8944 5.97852 18.9479 7.34307C19.0014 8.70763 18.5376 10.042 17.6492 11.0792L11.1783 17.5708C10.881 17.8682 10.4823 18.0423 10.0621 18.058C9.64188 18.0738 9.23124 17.9301 8.9125 17.6558L8.82083 17.5717L2.35083 11.0792C1.46291 10.0429 0.998816 8.70975 1.05135 7.34615C1.10388 5.98255 1.66915 4.68901 2.63417 3.72417V3.72417ZM3.8125 4.9025C3.13104 5.58415 2.73821 6.50204 2.71556 7.46564C2.6929 8.42925 3.04216 9.36457 3.69083 10.0775L3.8125 10.2058L10 16.3933L14.4192 11.9733L11.4733 9.0275L10.59 9.91083C10.3579 10.1431 10.0823 10.3273 9.779 10.453C9.47569 10.5787 9.15059 10.6435 8.82226 10.6435C8.15916 10.6437 7.52316 10.3804 7.05417 9.91167C6.58518 9.4429 6.32161 8.80702 6.32145 8.14392C6.3213 7.48082 6.58456 6.84482 7.05333 6.37583L8.805 4.62333C8.10789 4.06698 7.23534 3.77724 6.3439 3.80611C5.45247 3.83497 4.60049 4.18055 3.94083 4.78083L3.8125 4.9025V4.9025ZM10.8842 7.25917C11.0404 7.10294 11.2524 7.01518 11.4733 7.01518C11.6943 7.01518 11.9062 7.10294 12.0625 7.25917L15.5975 10.7942L16.1875 10.2058C16.8804 9.51344 17.2748 8.57743 17.2865 7.59797C17.2982 6.6185 16.9262 5.67335 16.25 4.96463C15.5739 4.25591 14.6472 3.83989 13.6683 3.80554C12.6894 3.7712 11.7358 4.12125 11.0117 4.78083L10.8842 4.9025L8.2325 7.55417C8.08806 7.69851 8.00164 7.89087 7.98966 8.09472C7.97768 8.29857 8.04096 8.49973 8.1675 8.66L8.2325 8.7325C8.37685 8.87694 8.5692 8.96335 8.77305 8.97534C8.9769 8.98732 9.17806 8.92404 9.33833 8.7975L9.41083 8.7325L10.8842 7.25917V7.25917Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Remix "shield-flash-line" (20px grid) — Модерация. */
export function RemixShieldFlashLineIcon({ className, ...props }: SidebarIconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="none"
      className={cn('size-5 shrink-0', className)}
      {...props}
    >
      <path
        d="M3.1525 2.355L10 0.833333L16.8475 2.355C17.0326 2.39614 17.198 2.49914 17.3167 2.647C17.4353 2.79487 17.5 2.97876 17.5 3.16833V11.4908C17.4999 12.3139 17.2967 13.1243 16.9082 13.85C16.5198 14.5757 15.9582 15.1943 15.2733 15.6508L10 19.1667L4.72667 15.6508C4.04189 15.1944 3.48038 14.5759 3.09196 13.8504C2.70353 13.1248 2.5002 12.3146 2.5 11.4917V3.16833C2.50003 2.97876 2.5647 2.79487 2.68332 2.647C2.80195 2.49914 2.96745 2.39614 3.1525 2.355V2.355ZM4.16667 3.83667V11.4908C4.16668 12.0395 4.30215 12.5798 4.56105 13.0636C4.81996 13.5474 5.19429 13.9598 5.65083 14.2642L10 17.1642L14.3492 14.2642C14.8056 13.9598 15.1799 13.5476 15.4388 13.0639C15.6977 12.5803 15.8332 12.0402 15.8333 11.4917V3.83667L10 2.54167L4.16667 3.83667ZM10.8333 8.33333H13.3333L9.16667 14.1667V10H6.66667L10.8333 4.16667V8.33333Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Remix "restaurant-2-line" (20px grid) — Витрина. */
export function RemixRestaurant2LineIcon({ className, ...props }: SidebarIconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="none"
      className={cn('size-5 shrink-0', className)}
      {...props}
    >
      <path
        d="M11.89 10.1217L11.1783 10.8333L17.0708 16.7258L15.8925 17.9042L10 12.0125L4.1075 17.9042L2.92917 16.7258L10.7117 8.94333C10.2217 7.72917 10.7283 5.98083 12.0617 4.64583C13.6892 3.01917 15.9317 2.62333 17.0708 3.7625C18.21 4.90167 17.8142 7.14417 16.1875 8.77083C14.8525 10.1058 13.1042 10.6125 11.89 10.1217V10.1217ZM3.51833 3.17333L9.11583 8.77083L6.75917 11.1283L3.5175 7.88667C2.8926 7.26157 2.54155 6.41388 2.54155 5.53C2.54155 4.64612 2.8926 3.79843 3.5175 3.17333H3.51833ZM15.0083 7.59167C16.0567 6.54417 16.2725 5.32 15.8925 4.94C15.5125 4.56 14.2883 4.77583 13.2408 5.82333C12.1933 6.87167 11.9775 8.09583 12.3575 8.47583C12.7367 8.855 13.9608 8.63917 15.0083 7.59167Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Remix "shopping-basket-line" (20px grid) — Категория. */
export function RemixShoppingBasketLineIcon({ className, ...props }: SidebarIconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="none"
      className={cn('size-5 shrink-0', className)}
      {...props}
    >
      <path
        d="M10 1.66667C11.3261 1.66667 12.5979 2.19345 13.5355 3.13113C14.4732 4.06881 15 5.34058 15 6.66667V7.5H18.3333V9.16667H17.3608L16.73 16.7358C16.7127 16.9441 16.6177 17.1382 16.464 17.2797C16.3103 17.4213 16.109 17.4999 15.9 17.5H4.1C3.89104 17.4999 3.68975 17.4213 3.53602 17.2797C3.38229 17.1382 3.28734 16.9441 3.27 16.7358L2.63833 9.16667H1.66667V7.5H5V6.66667C5 5.34058 5.52678 4.06881 6.46447 3.13113C7.40215 2.19345 8.67392 1.66667 10 1.66667V1.66667ZM15.6883 9.16667H4.31083L4.86667 15.8333H15.1325L15.6883 9.16667ZM10.8333 10.8333V14.1667H9.16667V10.8333H10.8333ZM7.5 10.8333V14.1667H5.83333V10.8333H7.5ZM14.1667 10.8333V14.1667H12.5V10.8333H14.1667ZM10 3.33333C9.1448 3.33333 8.32231 3.66203 7.70266 4.25144C7.083 4.84084 6.71359 5.64587 6.67083 6.5L6.66667 6.66667V7.5H13.3333V6.66667C13.3333 5.81146 13.0046 4.98898 12.4152 4.36932C11.8258 3.74967 11.0208 3.38026 10.1667 3.3375L10 3.33333Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Remix "star-line" (20px grid) — Скидки. */
export function RemixStarLineIcon({ className, ...props }: SidebarIconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="none"
      className={cn('size-5 shrink-0', className)}
      {...props}
    >
      <path
        d="M10 15.2167L4.1225 18.5067L5.435 11.9L0.489167 7.32667L7.17833 6.53333L10 0.416667L12.8217 6.53333L19.5108 7.32667L14.565 11.9L15.8775 18.5067L10 15.2167ZM10 13.3067L13.5392 15.2875L12.7483 11.31L15.7258 8.55583L11.6983 8.07833L10 4.39583L8.30167 8.07917L4.27417 8.55583L7.25167 11.31L6.46083 15.2875L10 13.3067V13.3067Z"
        fill="currentColor"
      />
    </svg>
  );
}

/**
 * Remix "settings-line" (stock 24px grid) — Настройки. Not part of the Figma
 * frame; matches the Remix line language for the added services group.
 */
export function RemixSettingsLineIcon({ className, ...props }: SidebarIconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={cn('size-5 shrink-0', className)}
      {...props}
    >
      <path d="M12 1L21.5 6.5V17.5L12 23L2.5 17.5V6.5L12 1ZM12 3.311L4.5 7.65311V16.3469L12 20.689L19.5 16.3469V7.65311L12 3.311ZM12 16C9.79086 16 8 14.2091 8 12C8 9.79086 9.79086 8 12 8C14.2091 8 16 9.79086 16 12C16 14.2091 14.2091 16 12 16ZM12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z" />
    </svg>
  );
}

/** Remix "sidebar-fold-line" (24px grid) — collapse toggle in the header. */
export function RemixSidebarFoldIcon({ className, ...props }: SidebarIconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={cn('size-4 shrink-0', className)}
      {...props}
    >
      <path d="M5 5H13V19H5V5ZM19 19H15V5H19V19ZM4 3C3.44772 3 3 3.44772 3 4V20C3 20.5523 3.44772 21 4 21H20C20.5523 21 21 20.5523 21 20V4C21 3.44772 20.5523 3 20 3H4ZM7 12L11 8.5V15.5L7 12Z" />
    </svg>
  );
}

/** Remix "sidebar-unfold-line" (24px grid) — expand toggle on the rail. */
export function RemixSidebarUnfoldIcon({ className, ...props }: SidebarIconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={cn('size-4 shrink-0', className)}
      {...props}
    >
      <path d="M5 5H13V19H5V5ZM19 19H15V5H19V19ZM4 3C3.44772 3 3 3.44772 3 4V20C3 20.5523 3.44772 21 4 21H20C20.5523 21 21 20.5523 21 20V4C21 3.44772 20.5523 3 20 3H4ZM11 12L7 8.5V15.5L11 12Z" />
    </svg>
  );
}
